import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import CalendarFilter from '../components/CalendarFilter';
import CSVDownloader from '../components/CSVDownloader';
import AlertsList from '../components/AlertsList';
import '../App.css';

ChartJS.register(LineElement, PointElement, LinearScale, CategoryScale, Tooltip, Legend, Filler);

const BUILDING_TOTAL_VALUE = '__building_total__';
const PARAMETER_META = {
  p24: {
    label: 'Inverter Power',
    unit: 'kW',
    rowKey: 'inv_power',
    convert: (value) => Number(value) / 1000,
  },
  p1: {
    label: 'Daily Energy',
    unit: 'kWh',
    rowKey: 'daily_energy',
    convert: (value) => Number(value) / 1000,
  },
  p2: {
    label: 'Total Energy',
    unit: 'kWh',
    rowKey: 'total_energy',
    convert: (value) => Number(value) / 1000,
  },
};

function formatDisplayNumber(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '-';
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatCsvNumber(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  return num.toFixed(2);
}

function aggregateHistoryRows(rows) {
  const grouped = new Map();
  rows.forEach((row) => {
    const date = `${row.date}`;
    if (!grouped.has(date)) {
      grouped.set(date, {
        date,
        inv_power: 0,
        daily_energy: 0,
        total_energy: 0,
      });
    }
    const target = grouped.get(date);
    target.inv_power += Number(row.inv_power) || 0;
    target.daily_energy += Number(row.daily_energy) || 0;
    target.total_energy += Number(row.total_energy) || 0;
  });
  return Array.from(grouped.values()).sort((a, b) => `${a.date}`.localeCompare(`${b.date}`));
}

function mapHistoryRowsToSeries(rows, parameter, formatLabelDate) {
  const meta = PARAMETER_META[parameter];
  if (!meta) return [];
  return rows
    .filter((row) => row[meta.rowKey] != null)
    .map((row) => ({
      x: formatLabelDate(row.date),
      y: meta.convert(row[meta.rowKey]),
    }))
    .filter((point) => Number.isFinite(point.y))
    .sort((a, b) => (a.x > b.x ? 1 : -1));
}

export default function AnalyticsPage({ phases }) {
  const { phaseId } = useParams();
  const navigate = useNavigate();
  const phase = useMemo(() => phases.find((p) => p.phaseId === phaseId), [phases, phaseId]);
  const phaseBackendId = phase?.phaseId || '';
  const phaseDataName = phase?.name || '';
  const phaseDisplayName = phase?.displayName || phase?.name || 'Phase';

  const [devices, setDevices] = useState([]);
  const filteredDevices = useMemo(
    () => devices.filter((d) => !phase || d.phase_id === phaseBackendId || d.ps_name === phaseDataName),
    [devices, phase, phaseBackendId, phaseDataName],
  );
  const buildingDeviceIds = useMemo(
    () => filteredDevices.map((device) => device.ps_key).filter(Boolean),
    [filteredDevices],
  );
  const [filters, setFilters] = useState({
    deviceId: BUILDING_TOTAL_VALUE,
    parameter: 'p24',
    startDate: '',
    endDate: '',
  });
  const [granularity, setGranularity] = useState('daily');
  const [dataPoints, setDataPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [datasetLabel, setDatasetLabel] = useState('');
  const [faultAlerts, setFaultAlerts] = useState([]);
  const socketUrl = '/inokom/ws';
  const socket = useMemo(() => io({ path: `${socketUrl}/socket.io`, transports: ['websocket', 'polling'] }), []);

  const parameterOptions = [
    { value: 'p24', label: 'Inverter Power' },
    { value: 'p1', label: 'Daily Energy' },
    { value: 'p2', label: 'Total Energy' },
  ];
  const isAggregatedEnergyOnly = granularity === 'monthly' || granularity === 'yearly';
  const isBuildingSelection = filters.deviceId === BUILDING_TOTAL_VALUE;
  const parameterDisplayName = (param) => {
    const meta = PARAMETER_META[param];
    return meta ? `${meta.label} (${meta.unit})` : param;
  };
  const parameterUnit = (param) => PARAMETER_META[param]?.unit || '';

  const API_BASE = '/inokom/api';
  const selectedDevice = useMemo(
    () => (isBuildingSelection ? null : filteredDevices.find((d) => d.ps_key === filters.deviceId) || null),
    [filteredDevices, filters.deviceId, isBuildingSelection],
  );
  const activeSourceLabel = isBuildingSelection
    ? `${phaseDisplayName || 'Building'} Combined Total`
    : (selectedDevice?.device_name || selectedDevice?.ps_name || selectedDevice?.ps_key || '');
  const currentBuildingTotals = useMemo(() => {
    const totals = filteredDevices.reduce(
      (acc, device) => {
        acc.inv_power += Number(device.inv_power) || 0;
        acc.daily_energy += Number(device.daily_energy) || 0;
        acc.total_energy += Number(device.total_energy) || 0;
        return acc;
      },
      { inv_power: 0, daily_energy: 0, total_energy: 0 },
    );
    return {
      inverterPower: PARAMETER_META.p24.convert(totals.inv_power),
      dailyEnergy: PARAMETER_META.p1.convert(totals.daily_energy),
      totalEnergy: PARAMETER_META.p2.convert(totals.total_energy),
    };
  }, [filteredDevices]);

  const formatLabelDate = (d) => {
    if (!d) return '';
    if (d.length === 8) return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
    if (d.length >= 14) return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)} ${d.slice(8, 10)}:${d.slice(10, 12)}`;
    return d;
  };

  const downsample = (points, maxPoints = 180) => {
    if (points.length <= maxPoints) return points;
    const step = Math.ceil(points.length / maxPoints);
    return points.filter((_, idx) => idx % step === 0);
  };

  const buildHistoryRequest = (includeAllParameters = false) => {
    const makeTs = (value, isStart) => {
      if (granularity === '24h' || granularity === 'daily') {
        return `${value.replace(/-/g, '')}${isStart ? '000000' : '235959'}`;
      }
      if (granularity === 'monthly') {
        const [y, m] = value.split('-');
        const lastDay = new Date(Number(y), Number(m), 0).getDate();
        return `${y}${m}${isStart ? '01' : String(lastDay).padStart(2, '0')}${isStart ? '000000' : '235959'}`;
      }
      return `${value}${isStart ? '0101000000' : '1231235959'}`;
    };

    const sourceKeys = isBuildingSelection ? buildingDeviceIds : [filters.deviceId];

    if (granularity === '24h') {
      return {
        granularity,
        ps_key_list: sourceKeys,
        points: includeAllParameters ? 'p1,p2,p24' : filters.parameter,
        start_time_stamp: makeTs(filters.startDate, true),
        end_time_stamp: makeTs(filters.endDate, false),
        minute_interval: '5',
      };
    }

    const queryType = granularity === 'daily' ? '1' : granularity === 'monthly' ? '2' : '3';
    const dataType = granularity === 'daily' ? '2' : '4';
    return {
      granularity,
      ps_key_list: sourceKeys,
      data_point: includeAllParameters
        ? 'p1,p2,p24'
        : (granularity === 'monthly' || granularity === 'yearly' ? 'p1' : 'p1,p2,p24'),
      start_time: filters.startDate.replace(/-/g, ''),
      end_time: filters.endDate.replace(/-/g, ''),
      query_type: queryType,
      data_type: dataType,
      order: '0',
    };
  };

  useEffect(() => {
    const today = new Date();
    const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
    setFilters((f) => ({
      ...f,
      startDate: sevenDaysAgo.toISOString().slice(0, 10),
      endDate: today.toISOString().slice(0, 10),
    }));

    let isDisposed = false;
    const loadDevices = async () => {
      try {
        const res = await fetch(`${API_BASE}/inverters/devices`);
        const list = await res.json();
        if (!isDisposed) setDevices(list);
      } catch (_e) {
        if (!isDisposed) setError('Failed to load devices');
      }
    };

    loadDevices();
    const intervalId = window.setInterval(loadDevices, 60000);
    return () => {
      isDisposed = true;
      window.clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const faultHandler = (rows) => setFaultAlerts(Array.isArray(rows) ? rows : []);
    const errorHandler = () => setFaultAlerts([]);
    socket.on('faultAlerts', faultHandler);
    socket.on('faultAlerts:error', errorHandler);
    return () => {
      socket.off('faultAlerts', faultHandler);
      socket.off('faultAlerts:error', errorHandler);
      socket.disconnect();
    };
  }, [socket]);

  useEffect(() => {
    socket.emit('faultAlerts:request', phase ? { phase_id: phaseBackendId, ps_name: phaseDataName } : {});
  }, [socket, phase, phaseBackendId, phaseDataName]);

  useEffect(() => {
    setFilters((f) => {
      if (!filteredDevices.length) return { ...f, deviceId: '' };
      if (f.deviceId === BUILDING_TOTAL_VALUE) return f;
      if (filteredDevices.some((device) => device.ps_key === f.deviceId)) return f;
      return { ...f, deviceId: BUILDING_TOTAL_VALUE };
    });
  }, [filteredDevices]);

  const handleGranularityChange = (mode) => {
    setGranularity(mode);
    if (mode === 'monthly' || mode === 'yearly') {
      setFilters((f) => ({ ...f, parameter: 'p1' }));
    }
    const today = new Date();
    if (mode === 'daily') {
      const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      setFilters((f) => ({
        ...f,
        startDate: weekAgo.toISOString().slice(0, 10),
        endDate: today.toISOString().slice(0, 10),
      }));
    } else if (mode === '24h') {
      const val = today.toISOString().slice(0, 10);
      setFilters((f) => ({ ...f, startDate: val, endDate: val }));
    } else if (mode === 'monthly') {
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const val = `${today.getFullYear()}-${month}`;
      setFilters((f) => ({ ...f, startDate: val, endDate: val }));
    } else {
      const year = String(today.getFullYear());
      setFilters((f) => ({ ...f, startDate: year, endDate: year }));
    }
  };

  const handleRun = async () => {
    setError('');
    if ((!filters.deviceId && !isBuildingSelection) || !filters.startDate || !filters.endDate) {
      setError('Please choose a source and date range');
      return;
    }
    if (isBuildingSelection && !buildingDeviceIds.length) {
      setError('No inverter data available for this building');
      return;
    }
    if (granularity === 'daily') {
      const from = filters.startDate.slice(0, 7);
      const to = filters.endDate.slice(0, 7);
      if (from !== to) {
        setError('For daily view, pick days within the same month');
        return;
      }
    }
    if (granularity === '24h' && filters.startDate !== filters.endDate) {
      setError('24h mode requires the same From and To date');
      return;
    }
    if (granularity === 'monthly') {
      const fromYear = filters.startDate.slice(0, 4);
      const toYear = filters.endDate.slice(0, 4);
      if (fromYear !== toYear) {
        setError('For monthly view, pick months within the same year');
        return;
      }
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/inverters/history`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildHistoryRequest(false)),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const { rows } = await res.json();
      const scopedRows = rows
        .filter((row) => (isBuildingSelection ? buildingDeviceIds.includes(row.ps_key) : row.ps_key === filters.deviceId))
        .sort((a, b) => `${a.date}`.localeCompare(`${b.date}`));
      const normalizedRows = isBuildingSelection ? aggregateHistoryRows(scopedRows) : scopedRows;
      const series = mapHistoryRowsToSeries(normalizedRows, filters.parameter, formatLabelDate);
      setDatasetLabel(filters.parameter);
      setDataPoints(downsample(series));
    } catch (_e) {
      setError('Failed to fetch history');
      setDataPoints([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCsv = () => {
    const run = async () => {
      try {
        setError('');
        const res = await fetch(`${API_BASE}/inverters/history`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(buildHistoryRequest(true)),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const { rows } = await res.json();
        const scopedRows = rows
          .filter((row) => (isBuildingSelection ? buildingDeviceIds.includes(row.ps_key) : row.ps_key === filters.deviceId))
          .sort((a, b) => `${a.date}`.localeCompare(`${b.date}`));
        const normalizedRows = isBuildingSelection ? aggregateHistoryRows(scopedRows) : scopedRows;
        if (!normalizedRows.length) {
          setError('No analytics data to download');
          return;
        }
        const lines = [
          ['Timestamp', 'Inv_Power (kW)', 'Daily Energy (kWh)', 'Total Energy (kWh)'].join(','),
          ...normalizedRows.map((row) => [
            `"${formatLabelDate(row.date)}"`,
            formatCsvNumber(PARAMETER_META.p24.convert(row.inv_power ?? 0)),
            formatCsvNumber(PARAMETER_META.p1.convert(row.daily_energy ?? 0)),
            formatCsvNumber(PARAMETER_META.p2.convert(row.total_energy ?? 0)),
          ].join(',')),
        ];
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const rangeLabel = filters.startDate === filters.endDate
          ? filters.startDate
          : `${filters.startDate}_to_${filters.endDate}`;
        const safe = (value) => `${value || ''}`.replace(/[^\w.-]+/g, '_');
        const sourceKey = isBuildingSelection ? 'building_total' : filters.deviceId;
        const fileName = `${safe(rangeLabel)}_${safe(activeSourceLabel || phaseDisplayName)}_${safe(sourceKey)}.csv`;
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (_e) {
        setError('Failed to download CSV');
      }
    };
    run();
  };

  const renderChart = () => {
    if (!dataPoints.length) return <div className="chart-placeholder">No data yet. Run analytics to load history.</div>;

    const labels = dataPoints.map((point) => point.x);
    const data = {
      labels,
      datasets: [
        {
          label: `${activeSourceLabel} - ${parameterDisplayName(datasetLabel || filters.parameter)}`,
          data: dataPoints.map((point) => point.y),
          tension: 0.25,
          borderColor: '#4fd1c5',
          backgroundColor: 'rgba(79, 209, 197, 0.15)',
          fill: true,
          pointRadius: granularity === '24h' ? 2 : 3,
          pointHoverRadius: 7,
          pointHitRadius: 12,
          spanGaps: true,
        },
      ],
    };

    const options = {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'nearest', intersect: false, axis: 'x' },
      plugins: {
        legend: { display: true, labels: { color: '#e5e7eb', font: { size: 14 } } },
        tooltip: {
          enabled: true,
          filter: (ctx) => ctx?.parsed?.y != null && !Number.isNaN(ctx.parsed.y),
          callbacks: {
            title: (items) => (items && items.length ? `Timestamp: ${items[0].label ?? ''}` : ''),
            label: (item) => {
              if (item?.parsed?.y == null) return '';
              return `Value: ${formatDisplayNumber(item.parsed.y)} ${parameterUnit(datasetLabel || filters.parameter)}`;
            },
            footer: () => `Parameter: ${parameterDisplayName(datasetLabel || filters.parameter)}`,
          },
          titleFont: { size: 16 },
          bodyFont: { size: 16 },
          footerFont: { size: 16 },
          boxPadding: 6,
          bodySpacing: 6,
          footerSpacing: 6,
        },
      },
      scales: {
        x: {
          ticks: { color: '#e5e7eb', maxRotation: 45, minRotation: 0 },
          title: { display: true, text: 'Time', color: '#e5e7eb', font: { size: 15 } },
        },
        y: {
          ticks: { color: '#e5e7eb' },
          title: {
            display: true,
            text: parameterDisplayName(datasetLabel || filters.parameter),
            color: '#e5e7eb',
            font: { size: 15 },
          },
        },
      },
    };

    return (
      <div style={{ height: 420, width: '100%' }}>
        <Line data={data} options={options} />
      </div>
    );
  };

  return (
    <div className="analytics-page">
      <div className="analytics-header">
        <div>
          <p className="eyebrow">Analytics - {phaseDisplayName}</p>
        </div>
        <button className="ghost-button" onClick={() => navigate(-1)}>
          Back
        </button>
      </div>

      <div className="analytics-grid">
        <div className="filter-card glass">
          <p className="eyebrow">Device & parameters</p>
          <div className="filter-row">
            <label>
              Source
              <select
                value={filters.deviceId}
                onChange={(e) => setFilters((f) => ({ ...f, deviceId: e.target.value }))}
                style={{ cursor: 'pointer' }}
              >
                {!filters.deviceId && <option value="">Select source</option>}
                {buildingDeviceIds.length ? (
                  <option value={BUILDING_TOTAL_VALUE}>
                    {phaseDisplayName || 'Building'} - Total
                  </option>
                ) : null}
                {filteredDevices.map((device) => (
                  <option key={device.ps_key} value={device.ps_key}>
                    {device.device_name || device.ps_name || device.ps_key}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Parameter
              <select
                value={filters.parameter}
                onChange={(e) => setFilters((f) => ({ ...f, parameter: e.target.value }))}
                style={{ cursor: 'pointer' }}
              >
                {parameterOptions.map((option) => (
                  <option
                    key={option.value}
                    value={option.value}
                    disabled={isAggregatedEnergyOnly && option.value !== 'p1'}
                  >
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="filter-row" style={{ marginTop: 8 }}>
            <label>
              Granularity
              <select value={granularity} onChange={(e) => handleGranularityChange(e.target.value)} style={{ cursor: 'pointer' }}>
                <option value="24h">24h</option>
                <option value="daily">Daily</option>
                <option value="monthly">Monthly (accumulated daily energy only)</option>
                <option value="yearly">Yearly (accumulated daily energy only)</option>
              </select>
            </label>
          </div>
          <CalendarFilter
            startDate={filters.startDate}
            endDate={filters.endDate}
            mode={granularity}
            onChange={({ startDate, endDate }) => setFilters((f) => ({ ...f, startDate, endDate }))}
          />
          <div className="filter-actions">
            <button className="primary-button" onClick={handleRun} disabled={loading}>
              {loading ? 'Loading...' : 'Run analytics'}
            </button>
            <CSVDownloader onDownload={handleDownloadCsv} />
          </div>
          {error && <p className="error-text">{error}</p>}
        </div>

        <div className="summary-strip">
          <div className="summary-card-mini glass">
            <div className="summary-card-mini__header">
              <p className="eyebrow">Building combined values</p>
              <span className="live-pill" aria-label="Live data">
                <span className="live-pill__dot" />
                Live
              </span>
            </div>
            <div className="building-summary-grid">
              <div className="building-metric-card building-metric-card--power">
                <span className="summary-label">Inverter Power</span>
                <strong>{formatDisplayNumber(currentBuildingTotals.inverterPower)} kW</strong>
              </div>
              <div className="building-metric-card building-metric-card--daily">
                <span className="summary-label">Daily Energy</span>
                <strong>{formatDisplayNumber(currentBuildingTotals.dailyEnergy)} kWh</strong>
              </div>
              <div className="building-metric-card building-metric-card--total">
                <span className="summary-label">Total Energy</span>
                <strong>{formatDisplayNumber(currentBuildingTotals.totalEnergy)} kWh</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="chart-card glass">
          <p className="eyebrow">Chart</p>
          <p className="chart-context">{activeSourceLabel || 'No source selected'}</p>
          {renderChart()}
        </div>

        <AlertsList alerts={faultAlerts} />
      </div>
    </div>
  );
}
